import { ArrowRight, Loader2, Pencil, Trash2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import type { OptimizedProfile } from "~/hooks/useOptimizedProfiles";
import type { ReceptionModificationRequest } from "~/types/receptionModification";
import { formatDate } from "~/utils/helpers";

import { ReceptionModificationStatusBadge } from "./ReceptionModificationStatusBadge";

interface ReceptionModificationDetailSheetProps {
  request: ReceptionModificationRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApproveClick: () => void;
  onRejectClick: () => void;
  isApprover: boolean;
  isApproving: boolean;
  currentUserId?: string | null;
  profiles: Record<string, OptimizedProfile>;
}

const num = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : v.toLocaleString("es-CL", { maximumFractionDigits: 2 });

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-200 py-1.5 text-sm last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="max-w-[60%] text-right font-medium text-gray-800">
        {value || "-"}
      </span>
    </div>
  );
}

function DiffRow({
  label,
  oldValue,
  newValue,
}: {
  label: string;
  oldValue: string;
  newValue: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-200 py-2 text-sm last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-red-700 line-through">{oldValue}</span>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="font-semibold text-green-700">{newValue}</span>
      </span>
    </div>
  );
}

export function ReceptionModificationDetailSheet({
  request,
  open,
  onOpenChange,
  onApproveClick,
  onRejectClick,
  isApprover,
  isApproving,
  currentUserId,
  profiles,
}: ReceptionModificationDetailSheetProps) {
  if (!request) return null;

  const isSelfRequest =
    !!request.requested_by && request.requested_by === currentUserId;
  const requesterName =
    (request.requested_by && profiles[request.requested_by]?.full_name) ||
    "Desconocido";
  const reviewerName =
    request.reviewed_by && profiles[request.reviewed_by]?.full_name;

  const unit = request.unit || "";
  const isDelete = request.request_type === "delete";

  // Net change on the reception's plant stock when approved (in reception units).
  // Only meaningful when reception unit == stock unit (raw material / product).
  const plantIsP1 = request.plant === "P1";
  const planStockNow = plantIsP1 ? request.stock_p1 : request.stock_p2;
  const netDelta = isDelete
    ? -(request.current_quantity ?? 0)
    : (request.new_quantity ?? 0) - (request.current_quantity ?? 0);
  const showResulting =
    request.ingredient_type !== "oil" &&
    planStockNow !== null &&
    planStockNow !== undefined;
  const resulting = (planStockNow ?? 0) + netDelta;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <div className="flex flex-wrap items-center gap-3">
            <SheetTitle>{request.item_name || "Ítem"}</SheetTitle>
            <ReceptionModificationStatusBadge status={request.status} />
            <Badge
              variant="outline"
              className={
                isDelete
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-blue-200 bg-blue-50 text-blue-700"
              }
            >
              {isDelete ? (
                <Trash2 className="mr-1 h-3 w-3" />
              ) : (
                <Pencil className="mr-1 h-3 w-3" />
              )}
              {isDelete ? "Eliminación" : "Edición"}
            </Badge>
          </div>
          <SheetDescription>
            Solicitud de modificación de recepción pendiente de autorización.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
            <InfoRow
              label="Orden de compra"
              value={
                request.po_number
                  ? `OC #${String(request.po_number).padStart(5, "0")}`
                  : null
              }
            />
            <InfoRow label="Proveedor" value={request.supplier_name} />
            <InfoRow label="Planta" value={request.plant} />
            <InfoRow label="Solicitado por" value={requesterName} />
            <InfoRow label="Fecha" value={formatDate(request.requested_at)} />
            <InfoRow label="Motivo" value={request.reason} />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">
              {isDelete ? "Eliminación solicitada" : "Cambios solicitados"}
            </h3>
            <div className="rounded-lg border border-gray-300 bg-white px-4 py-2">
              {isDelete ? (
                <p className="py-2 text-sm text-gray-700">
                  Eliminar la línea de{" "}
                  <span className="font-semibold">
                    {num(request.current_quantity)} {unit}
                  </span>
                  . Al aprobar se revierte el stock ingresado y la línea de la OC
                  vuelve a quedar pendiente.
                </p>
              ) : (
                <>
                  <DiffRow
                    label={`Cantidad (${unit})`}
                    oldValue={num(request.current_quantity)}
                    newValue={num(request.new_quantity)}
                  />
                  {request.new_unit_cost !== null && (
                    <DiffRow
                      label="Costo unitario"
                      oldValue={`$${num(request.current_unit_cost)}`}
                      newValue={`$${num(request.new_unit_cost)}`}
                    />
                  )}
                  {request.new_expiry_date && (
                    <DiffRow
                      label="Vencimiento"
                      oldValue={
                        request.current_expiry_date
                          ? formatDate(request.current_expiry_date)
                          : "—"
                      }
                      newValue={formatDate(request.new_expiry_date)}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Live stock of the material (what the approver judges against). */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">
              Stock actual del ítem
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-3 text-center">
                <p className="text-xs text-gray-500">P1</p>
                <p className="font-mono text-sm font-semibold text-gray-800">
                  {num(request.stock_p1)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-3 text-center">
                <p className="text-xs text-gray-500">P2</p>
                <p className="font-mono text-sm font-semibold text-gray-800">
                  {num(request.stock_p2)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-3 text-center">
                <p className="text-xs text-gray-500">Total</p>
                <p className="font-mono text-sm font-semibold text-gray-800">
                  {num(request.stock_total)}
                </p>
              </div>
            </div>
            {showResulting && (
              <p className="mt-2 text-sm text-gray-600">
                Stock en planta {request.plant} al aprobar:{" "}
                <span className="font-mono">{num(planStockNow)}</span>
                <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                <span
                  className={`font-mono font-semibold ${
                    resulting < 0 ? "text-red-700" : "text-gray-800"
                  }`}
                >
                  {num(resulting)}
                </span>
                {resulting < 0 && (
                  <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700">
                    Quedaría negativo
                  </span>
                )}
              </p>
            )}
          </div>

          {request.status !== "pending" && (
            <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
              <InfoRow
                label={
                  request.status === "approved"
                    ? "Aprobada por"
                    : "Rechazada por"
                }
                value={reviewerName}
              />
              <InfoRow
                label="Fecha resolución"
                value={formatDate(request.reviewed_at)}
              />
              {request.review_notes && (
                <InfoRow label="Notas" value={request.review_notes} />
              )}
            </div>
          )}

          {request.status === "pending" && isApprover && (
            <div className="space-y-2">
              {isSelfRequest && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  No puede autorizar su propia solicitud; debe hacerlo otra
                  persona.
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={onApproveClick}
                  disabled={isApproving || isSelfRequest}
                >
                  {isApproving && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Aprobar
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                  onClick={onRejectClick}
                  disabled={isApproving}
                >
                  Rechazar
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
